import { useEffect, useState } from "react";
import { X, Download, ExternalLink, AlertTriangle, Building2, Mic2 } from "lucide-react";
import { http, fmtDate, fmtDateTime, fmtRupee, errMsg, downloadFile, timeLeft } from "../api";
import AudioPlayer from "./AudioPlayer";

const TONE = { COMPLETED: "success", DECLINED: "danger", IN_PROGRESS: "", DELIVERED: "warning", REVISION_REQUESTED: "warning", PENDING_ARTIST_ACKNOWLEDGEMENT: "warning" };
export const stageLabel = (s) => ({
  PENDING_ARTIST_ACKNOWLEDGEMENT: "Awaiting acknowledgement", IN_PROGRESS: "In progress", DELIVERED: "Delivered · awaiting client",
  REVISION_REQUESTED: "Revision requested", COMPLETED: "Completed", DECLINED: "Declined",
}[s] || s);
export const StageBadge = ({ status }) => <span className={`badge ${TONE[status] ?? ""}`} data-testid="order-stage-badge">{stageLabel(status)}</span>;

export default function OrderDetailModal({ orderId, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [notice, setNotice] = useState("");
  const load = () => http.get(`/orders/${orderId}`).then(r => setData(r.data)).catch(e => setNotice(errMsg(e)));
  useEffect(() => { load(); }, [orderId]);

  const act = async (action) => {
    try { await http.post(`/orders/${orderId}/${action}`); setNotice("Updated"); load(); onChanged?.(); }
    catch (e) { setNotice(errMsg(e)); }
  };

  if (!data) return null;
  const o = data.order;
  const duration = o.actual_duration_hours || o.estimated_hours || 0;
  const payout = o.final_artist_payout ?? (o.artist_base_rate_snapshot || 0) * duration;
  const ack = o.status === "PENDING_ARTIST_ACKNOWLEDGEMENT" ? timeLeft(o.ack_deadline) : null;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal modal-lg" onClick={e => e.stopPropagation()} data-testid="order-detail-modal">
        <div className="modal-head">
          <div>
            <div style={{ fontSize: 11, color: "var(--muted)", fontWeight: 600 }}>{o.order_code}</div>
            <h2>{o.project_title}</h2>
          </div>
          <button className="modal-close" onClick={onClose} data-testid="order-detail-close"><X size={18} /></button>
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 12 }}>
          <StageBadge status={o.status} />
          {o.deadline_revision_status === "REQUESTED" && <span className="badge danger"><AlertTriangle size={12} /> Deadline revision pending</span>}
          {ack && <span className={`badge ${ack.tone}`}>Ack {ack.text}</span>}
        </div>
        {notice && <div className="notice">{notice}</div>}

        <div className="party-row">
          <div className="party"><Building2 size={16} /><div><small>Client</small><strong>{o.client_company || "Individual"}</strong><span>{o.client_name}</span></div></div>
          <div className="party-arrow">→</div>
          <div className="party"><Mic2 size={16} /><div><small>Artist</small><strong>{o.artist_name}</strong><span>{o.artist_public_id || ""}</span></div></div>
        </div>

        <div className="detail-grid">
          <div>
            <div className="section-title">PROJECT</div>
            <div className="kv"><span>Language / Category</span><span>{o.language} · {o.category}</span></div>
            <div className="kv"><span>{o.actual_duration_hours ? "Final duration" : "Estimated duration"}</span><span>{duration}h</span></div>
            <div className="kv"><span>Delivery format</span><span>{o.delivery_format || "—"}</span></div>
            <div className="kv"><span>Requested on</span><span>{fmtDateTime(o.created_at)}</span></div>
            <div className="kv"><span>Deadline</span><span>{fmtDate(o.deadline)}{o.original_deadline && o.original_deadline !== o.deadline ? ` (was ${fmtDate(o.original_deadline)})` : ""}</span></div>
            {o.accepted_at && <div className="kv"><span>Accepted on</span><span>{fmtDateTime(o.accepted_at)}</span></div>}
            {o.delivered_at && <div className="kv"><span>Delivered on</span><span>{fmtDateTime(o.delivered_at)}</span></div>}
            {o.completed_at && <div className="kv"><span>Client approved on</span><span>{fmtDateTime(o.completed_at)}</span></div>}
            {o.description && <p style={{ fontSize: 13, marginTop: 10, color: "var(--ink-2)" }}>{o.description}</p>}
            {o.instructions && <p style={{ fontSize: 12.5, marginTop: 6, color: "var(--muted)" }}>Instructions: {o.instructions}</p>}
          </div>
          <div>
            <div className="section-title">MONEY (ADMIN VIEW)</div>
            <div className="kv"><span>Client pays</span><span>{fmtRupee(o.final_amount ?? o.estimated_amount)}</span></div>
            <div className="kv"><span>Artist payout</span><span>{fmtRupee(payout)}</span></div>
            <div className="kv"><span>Your commission</span><span style={{ color: "var(--success)" }}>{fmtRupee((o.final_amount ?? o.estimated_amount) - payout)}</span></div>
            <div className="kv"><span>Payment status</span><span>{o.payment_status || "UNPAID"}</span></div>
            {o.deadline_revision_status === "REQUESTED" && (
              <div style={{ marginTop: 12, padding: 12, background: "var(--danger-soft)", borderRadius: 10, fontSize: 13 }}>
                <strong style={{ color: "var(--danger)" }}>Artist wants new deadline: {fmtDate(o.requested_deadline)}</strong>
                <p style={{ marginTop: 4 }}>{o.deadline_revision_reason}</p>
                <div style={{ display: "flex", gap: 6, marginTop: 8 }}>
                  <button className="pill-action approve" onClick={() => act("deadline-approve")} data-testid="admin-approve-deadline">Approve</button>
                  <button className="pill-action reject" onClick={() => act("deadline-reject")} data-testid="admin-reject-deadline">Reject</button>
                </div>
              </div>
            )}
            {o.revision_note && <div style={{ marginTop: 10, fontSize: 12.5, color: "var(--warning)" }}>Revision note: {o.revision_note}</div>}
            {o.decline_reason && <div style={{ marginTop: 10, fontSize: 12.5, color: "var(--danger)" }}>Declined: {o.decline_reason}</div>}
          </div>
        </div>

        <div className="section-title">DELIVERIES ({data.deliveries.length})</div>
        {data.deliveries.length === 0 && <p style={{ fontSize: 13, color: "var(--muted)" }}>Nothing delivered yet.</p>}
        {data.deliveries.map(d => (
          <div key={d.id} className="list-row" style={{ padding: 10, marginBottom: 6 }} data-testid={`delivery-${d.id}`}>
            <div className="list-row-info"><strong>v{d.version} · {d.filename}</strong><p>{fmtDateTime(d.created_at)}{d.notes ? ` · ${d.notes}` : ""}</p></div>
            {d.drive_link ? <a className="ghost" href={d.drive_link} target="_blank" rel="noreferrer" data-testid={`admin-delivery-drive-link-${d.id}`}><ExternalLink size={13} /> Open Drive link</a> : <><AudioPlayer id={d.id} compact /><button className="ghost" onClick={() => downloadFile(`/files/${d.id}`, {}, d.filename)} data-testid={`download-delivery-${d.id}`}><Download size={13} /></button></>}
          </div>
        ))}

        <div className="section-title">TIMELINE</div>
        <ul className="timeline">
          {[...(o.timeline || [])].reverse().map((t, i) => (
            <li key={i}><span className="timeline-dot" /><div className="timeline-body"><strong>{t.event}</strong><small>{t.actor} · {fmtDateTime(t.created_at)}</small></div></li>
          ))}
        </ul>
      </div>
    </div>
  );
}
